import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import './styles/main.css';
import {
  APP_CONFIG,
  type QualityMode,
  QUALITY_SETTINGS,
  type VisualSettings,
  visualSettingsForQuality,
} from './config/appConfig';
import { DATA_SOURCES } from './config/dataSources';
import { flyToTokushimaCenter } from './cesium/camera';
import { createViewer } from './cesium/createViewer';
import {
  applyQuality,
  applyVisualSettings,
  configureEcoMode,
} from './cesium/performance';
import { createNightLighting } from './cesium/nightLighting';
import { createFirstPersonController } from './cesium/firstPersonController';
import { loadBuildingLod1 } from './layers/buildingLod1';
import { loadBuildingLod2 } from './layers/buildingLod2';
import {
  nightFactorForDate,
  setBuildingFacadeNightFactor,
  setBuildingFacadeQuality,
} from './layers/buildingFacadeShader';
import { loadOrthophoto } from './layers/ortho';
import { loadBaseTerrain } from './layers/terrain';
import { loadTerrainLod3 } from './layers/terrainLod3';
import { loadWaterSurface, type WaterLoadResult } from './layers/water';
import { loadRoads, type RoadLoadResult } from './layers/roads';
import { ControlPanel } from './ui/controlPanel';
import {
  InfoPanel,
  readFeatureProperties,
  selectedTileset,
} from './ui/infoPanel';
import { LoadingController } from './ui/loading';
import { LayerPanel } from './ui/layerPanel';

window.CESIUM_BASE_URL = '/cesiumStatic/';

const loading = new LoadingController();
const layers = new LayerPanel();
const info = new InfoPanel();

function formatError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const fields = ['message', 'statusCode', 'x', 'y', 'level', 'timesRetried', 'retry'];
    const summary = fields
      .filter((field) => record[field] !== undefined)
      .map((field) => `${field}=${String(record[field])}`)
      .join(', ');
    if (summary) return summary;
  }
  return String(error);
}

function recordDataError(label: string, url: string, error: unknown): void {
  const reason = formatError(error);
  console.error(`[PLATEAU ${label}] failed: ${reason}`, { url, reason });
  loading.showError(`${label}の取得に失敗しました。代替表示で継続します。`);
}

function setViewerTime(viewer: Cesium.Viewer, date: Date): void {
  viewer.clock.currentTime = Cesium.JulianDate.fromDate(date);
  viewer.clock.shouldAnimate = false;
  viewer.scene.requestRender();
}

function installFeaturePicking(
  viewer: Cesium.Viewer,
  buildingLod1: Cesium.Cesium3DTileset | null,
  getBuildingLod2: () => Cesium.Cesium3DTileset | undefined,
  getRoads: () => RoadLoadResult | undefined,
): void {
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
  handler.setInputAction(
    (movement: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const picked = viewer.scene.pick(movement.position);
      if (!picked) {
        info.hide();
        return;
      }

      const properties = readFeatureProperties(picked);
      const pickedTileset = selectedTileset(picked);
      const roadTileset = getRoads()?.tileset;
      const buildingLod2 = getBuildingLod2();
      const source = pickedTileset === roadTileset
        ? '道路LOD1（MVT）'
        : pickedTileset === buildingLod2
          ? '建築物LOD2'
          : pickedTileset === buildingLod1
            ? '建築物LOD1'
            : 'PLATEAU 3Dモデル';
      const title = pickedTileset === roadTileset ? '道路情報' : '建物情報';
      info.show(title, source, properties);
    },
    Cesium.ScreenSpaceEventType.LEFT_CLICK,
  );
}

async function loadAdvancedLayers(
  viewer: Cesium.Viewer,
  buildingLod1: Cesium.Cesium3DTileset | null,
  getQuality: () => QualityMode,
  setRoads: (result: RoadLoadResult) => void,
  setBuildingLod2: (tileset: Cesium.Cesium3DTileset) => void,
  setWater: (result: WaterLoadResult) => void,
): Promise<void> {
  let roadResult: RoadLoadResult | undefined;
  try {
    const road = await loadRoads(viewer, (error) => {
      layers.setStatus('roads', 'error', '道路なしで継続');
      recordDataError('道路LOD1', DATA_SOURCES.roads, error);
    });
    roadResult = road;
    setRoads(road);
    road.tileset.maximumScreenSpaceError =
      QUALITY_SETTINGS[getQuality()].roadMaximumScreenSpaceError;
    layers.setStatus('roads', 'ready', '中心部：区画線・立体橋梁（推定）');
    layers.setToggleHandler('roads', (visible) => road.setVisible(visible));
  } catch {
    // 道路取得失敗時は地形・建物だけで表示を継続する。
  }

  try {
    const water = await loadWaterSurface(viewer, (error) => {
      layers.setStatus('water', 'error', '水面データなしで継続');
      recordDataError('水面GeoJSON', DATA_SOURCES.waterGeoJson, error);
    });
    setWater(water);
    layers.setStatus('water', 'ready', 'Waterマテリアル / 水域ポリゴン');
    layers.setToggleHandler('water', (visible) => water.setVisible(visible));
  } catch {
    // 水面データ取得失敗時も地形・建物は継続する。
  }

  let buildingLod2: Awaited<ReturnType<typeof loadBuildingLod2>> | null = null;
  try {
    buildingLod2 = await loadBuildingLod2(viewer, buildingLod1, (error) => {
      layers.setStatus('building-lod2', 'error', 'LOD1表示を継続');
      recordDataError('建築物LOD2', DATA_SOURCES.buildingLod2, error);
    });
    setBuildingLod2(buildingLod2.tileset);
    const { coverage, mode } = buildingLod2;
    layers.setStatus(
      'building-lod2',
      'ready',
      `${mode === 'clipping' ? '矩形クリップ検証' : '表示切替'} / ${coverage.west.toFixed(3)}〜${coverage.east.toFixed(3)}°`,
    );
    layers.setToggleHandler('building-lod2', (visible) => {
      buildingLod2?.setVisible(visible);
    });
    console.info('[PLATEAU] LOD2 coverage', coverage);
  } catch {
    // LOD2取得失敗時もLOD1・Terrain・航空写真は継続する。
  }

  if (DATA_SOURCES.terrainLod3) {
    loading.update('検証済み地形LOD3を読み込んでいます', 94);
    try {
      const terrainLod3 = await loadTerrainLod3(viewer, (error) => {
        layers.setStatus('terrain-lod3', 'error', 'ベースTerrainを継続');
        recordDataError('地形LOD3', DATA_SOURCES.terrainLod3 ?? '', error);
      });
      layers.setStatus(
        'terrain-lod3',
        'ready',
        `対象範囲を置換 / ${terrainLod3.coverage.west.toFixed(3)}〜${terrainLod3.coverage.east.toFixed(3)}°`,
      );
      layers.setToggleHandler('terrain-lod3', (visible) => {
        terrainLod3.setVisible(visible);
      });
    } catch {
      // 検証済み成果物が取得できない場合はベースTerrainへ戻す。
    }
  }

  if (roadResult || buildingLod2) {
    console.info('[PLATEAU] Step 6 advanced layers ready');
  }
}

async function bootstrap(): Promise<void> {
  layers.add('terrain', 'PLATEAU-Terrain', () => undefined);
  layers.add('ortho', '航空写真（PLATEAU / GSI）', () => undefined);
  layers.add('building-lod1', '建築物 LOD1', () => undefined);
  layers.add('building-lod2', '建築物 LOD2', () => undefined);
  layers.add('roads', '道路 LOD1', () => undefined);
  layers.add('water', '水面（徳島市水域）', () => undefined);
  layers.add('terrain-lod3', '地形 LOD3', () => undefined);

  layers.setStatus('terrain', 'loading', 'Terrainを準備中');
  layers.setStatus('ortho', 'loading', '航空写真を準備中');
  layers.setStatus('building-lod1', 'loading', '建物を待機中');
  layers.setStatus('building-lod2', 'loading', '実範囲を検証待ち');
  layers.setStatus('roads', 'loading', '道路MVTを待機中');
  layers.setStatus('water', 'loading', '水域ポリゴンを読み込み中');
  layers.setStatus(
    'terrain-lod3',
    DATA_SOURCES.terrainLod3 ? 'loading' : 'disabled',
    DATA_SOURCES.terrainLod3 ? '検証済み成果物を確認中' : '検証済みURL未登録',
  );
  loading.update('Cesiumビューアを初期化しています', 8);

  let viewer: Cesium.Viewer;
  try {
    viewer = createViewer('cesiumContainer');
    configureEcoMode(viewer);
  } catch (error) {
    console.error('[Cesium viewer] failed', error);
    loading.update('3Dビューアを初期化できませんでした', 100);
    loading.showError('WebGLまたはCesiumの初期化に失敗しました。');
    return;
  }

  let quality: QualityMode = APP_CONFIG.defaultQuality;
  let roadResult: RoadLoadResult | undefined;
  let buildingLod2Tileset: Cesium.Cesium3DTileset | undefined;
  let buildingLod1: Cesium.Cesium3DTileset | null = null;
  let waterResult: WaterLoadResult | undefined;
  let visualSettings: VisualSettings = visualSettingsForQuality(quality);
  let currentDate = new Date();
  const nightLighting = createNightLighting(viewer);
  const firstPerson = createFirstPersonController(viewer, () => [
    buildingLod1,
    buildingLod2Tileset,
  ].filter((tileset): tileset is Cesium.Cesium3DTileset => Boolean(tileset)));

  const applyActiveQuality = (): void => {
    const allTilesets = [
      buildingLod1,
      buildingLod2Tileset,
      roadResult?.tileset,
      waterResult?.tileset,
    ]
      .filter((tileset): tileset is Cesium.Cesium3DTileset => Boolean(tileset));
    applyQuality(viewer, quality, allTilesets);
    applyVisualSettings(viewer, visualSettings, allTilesets);
    nightLighting.setEnabled(visualSettings.nightLighting);
    nightLighting.setTime(currentDate);
    if (buildingLod1) {
      setBuildingFacadeQuality(buildingLod1, quality);
      setBuildingFacadeNightFactor(
        buildingLod1,
        visualSettings.nightLighting ? nightFactorForDate(currentDate) : 0,
      );
    }
    if (buildingLod2Tileset) {
      buildingLod2Tileset.maximumScreenSpaceError =
        QUALITY_SETTINGS[quality].buildingLod2MaximumScreenSpaceError;
    }
    if (roadResult) {
      roadResult.tileset.maximumScreenSpaceError =
        QUALITY_SETTINGS[quality].roadMaximumScreenSpaceError;
      roadResult.tileset.shadows = Cesium.ShadowMode.DISABLED;
    }
    if (waterResult?.tileset) {
      waterResult.tileset.shadows = Cesium.ShadowMode.DISABLED;
    }
  };

  const controls = new ControlPanel({
    onQualityChange: (nextQuality) => {
      quality = nextQuality;
      visualSettings = visualSettingsForQuality(quality);
      controls.setVisualSettings(visualSettings);
      applyActiveQuality();
      console.info('[PLATEAU] quality changed', quality);
    },
    onTimeChange: (date) => {
      currentDate = date;
      setViewerTime(viewer, date);
      nightLighting.setTime(date);
      if (buildingLod1) {
        setBuildingFacadeNightFactor(
          buildingLod1,
          visualSettings.nightLighting ? nightFactorForDate(date) : 0,
        );
        viewer.scene.requestRender();
      }
    },
    onVisualSettingsChange: (nextSettings) => {
      visualSettings = nextSettings;
      applyVisualSettings(viewer, visualSettings, [
        buildingLod1,
        buildingLod2Tileset,
        roadResult?.tileset,
      ].filter((tileset): tileset is Cesium.Cesium3DTileset => Boolean(tileset)));
      nightLighting.setEnabled(visualSettings.nightLighting);
      nightLighting.setTime(currentDate);
      if (buildingLod1) {
        setBuildingFacadeNightFactor(
          buildingLod1,
          visualSettings.nightLighting ? nightFactorForDate(currentDate) : 0,
        );
        viewer.scene.requestRender();
      }
    },
    onViewModeChange: (mode) => {
      if (mode === 'first-person') firstPerson.enter();
      else firstPerson.exit();
      controls.setViewMode(firstPerson.isActive ? 'first-person' : 'overview');
    },
  });
  controls.setQuality(quality);
  controls.setVisualSettings(visualSettings);
  controls.setViewMode('overview');
  controls.setTime(currentDate);
  setViewerTime(viewer, currentDate);

  layers.setToggleHandler('terrain', (visible) => {
    viewer.scene.globe.show = visible;
    viewer.scene.requestRender();
  });

  console.info('[PLATEAU] Step 1-6 data sources', {
    cityCode: DATA_SOURCES.cityCode,
    retrievedAt: DATA_SOURCES.retrievedAt,
    terrain: DATA_SOURCES.terrain,
    ortho: DATA_SOURCES.ortho,
    gsiAerial: DATA_SOURCES.gsiAerial,
    buildingLod1: DATA_SOURCES.buildingLod1,
    buildingLod2: DATA_SOURCES.buildingLod2,
    roads: DATA_SOURCES.roads,
  });

  loading.update('徳島市のTerrainを読み込んでいます', 25);
  const terrain = await loadBaseTerrain(viewer, (error) => {
    layers.setStatus('terrain', 'fallback', '楕円体Terrainへ切替');
    recordDataError('PLATEAU-Terrain', DATA_SOURCES.terrain, error);
  });
  if (!terrain.isFallback) layers.setStatus('terrain', 'ready', '表示中');

  loading.update('徳島市の航空写真を読み込んでいます', 47);
  const ortho = await loadOrthophoto(viewer, (error, replacement) => {
    layers.setStatus('ortho', 'fallback', replacement);
    recordDataError('航空写真', DATA_SOURCES.ortho, error);
  });
  if (!ortho.isFallback) layers.setStatus('ortho', 'ready', ortho.source);

  loading.update('徳島市の建築物LOD1を読み込んでいます', 70);
  try {
    buildingLod1 = await loadBuildingLod1(viewer, (error) => {
      layers.setStatus('building-lod1', 'error', '建物なしで継続');
      recordDataError('建築物LOD1', DATA_SOURCES.buildingLod1, error);
    });
    firstPerson.setCollisionTilesets([buildingLod1]);
    layers.setStatus('building-lod1', 'ready', '表示中 / 屋根航空写真・外壁・窓');
    applyActiveQuality();
  } catch {
    // loadBuildingLod1で記録済み。Terrainと航空写真の表示は継続する。
  }

  layers.setToggleHandler('ortho', (visible) => ortho.setVisible(visible));
  if (buildingLod1) {
    layers.setToggleHandler('building-lod1', (visible) => {
      if (buildingLod1) buildingLod1.show = visible;
      viewer.scene.requestRender();
    });
  }

  loading.update('徳島市中心部へ移動しています', 80);
  await flyToTokushimaCenter(viewer);
  installFeaturePicking(
    viewer,
    buildingLod1,
    () => buildingLod2Tileset,
    () => roadResult,
  );

  // ベース地図・LOD1・カメラを先に表示し、道路やLOD2はバックグラウンドで続ける。
  // これにより初回にユーザーが操作できるまでの時間を短縮する。
  loading.update('徳島市3D空間を準備しています', 88);
  loading.complete();
  console.info('[PLATEAU] Step 6 base view ready', {
    quality,
    targetFps: '20fps以上（カメラ移動時の目標）',
    buildingLod1Loaded: Boolean(buildingLod1),
  });

  void loadAdvancedLayers(
    viewer,
    buildingLod1,
    () => quality,
    (result) => {
      roadResult = result;
      applyActiveQuality();
    },
    (tileset) => {
      buildingLod2Tileset = tileset;
      firstPerson.setCollisionTilesets([
        buildingLod1,
        buildingLod2Tileset,
      ].filter((candidate): candidate is Cesium.Cesium3DTileset => Boolean(candidate)));
      applyActiveQuality();
    },
    (result) => {
      waterResult = result;
      applyActiveQuality();
    },
  );
}

void bootstrap().catch((error) => {
  console.error('[PLATEAU bootstrap] unexpected error', error);
  loading.showError('予期しないエラーが発生しました。表示できるレイヤーで継続します。');
});
